import { useCallback, useEffect, useRef, useState } from "react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type { FetchedFile } from "@shared-core/data/customer-link-adapter";

// A file a read named (a proof's PDF, a page picture) cannot be pointed at by a link, frame or `<img>`: those
// send the session cookie but not the chosen-customer header, so a person with several customers is refused
// with `choose_customer`. It is fetched through the adapter instead, which sends both, and shown from an object
// URL. The URL is made here and revoked when the file changes or the page goes, as the staff shell's page
// pictures do.

/** Fetches a file the read named, for the page to show or save. */
export type FetchFile = (apiPath: string) => Promise<FetchedFile>;

export type FetchedFileState =
  | { status: "loading" }
  | { status: "failed" }
  | { status: "ready"; file: FetchedFile; objectUrl: string };

/**
 * The file at `apiPath`, fetched and ready to show: loading, then ready with an object URL or failed (a missing file,
 * a refusal, no network). `retry` asks again after a failure. A null path is nothing to fetch and stays loading.
 */
export function useFetchedFile(
  fetchFile: FetchFile,
  apiPath: string | null,
): { state: FetchedFileState; retry: () => void } {
  const [state, setState] = useState<FetchedFileState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  // The adapter's method is stable in the app; a story may pass a new function each render, which must not refetch.
  const fetchRef = useRef(fetchFile);
  useEffect(() => {
    fetchRef.current = fetchFile;
  });

  useEffect(() => {
    setState({ status: "loading" });
    if (apiPath === null) return undefined;
    let isCurrent = true;
    let objectUrl: string | null = null;
    fetchRef
      .current(apiPath)
      .then((file) => {
        if (!isCurrent) return;
        objectUrl = URL.createObjectURL(file.blob);
        setState({ status: "ready", file, objectUrl });
      })
      .catch(() => {
        if (isCurrent) setState({ status: "failed" });
      });
    return () => {
      isCurrent = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [apiPath, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, retry };
}

/**
 * Saves a file the read named: fetched through the adapter (so the chosen customer goes with it), then handed to the
 * platform to save. `hasFailed` is true until the next attempt, for the page to say so beside the button.
 */
export function useFileDownload(fetchFile: FetchFile): {
  isBusy: boolean;
  hasFailed: boolean;
  download: (apiPath: string, fallbackName: string) => Promise<void>;
} {
  const capabilities = usePlatformCapabilities();
  const [isBusy, setIsBusy] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const download = useCallback(
    async (apiPath: string, fallbackName: string) => {
      setHasFailed(false);
      setIsBusy(true);
      try {
        const file = await fetchFile(apiPath);
        capabilities.downloadBlob(file.filename ?? fallbackName, file.blob);
      } catch {
        setHasFailed(true);
      } finally {
        setIsBusy(false);
      }
    },
    [capabilities, fetchFile],
  );
  return { isBusy, hasFailed, download };
}
